// Texto por defecto de la página (specs/138-editor-contenido-paginas). Es la fuente del contenido
// mientras nadie lo edite en el panel; una edición guardada lo reemplaza por bloque.
//
// Búsquedas objetivo (Keyword Planner, Perú, nov. 2026): «stand de feria» (480/mes), «stand
// publicitario» (260), «stand para ferias» (210), «stands para ferias» (170), «diseño de stands»
// (70), «stands modulares» (40). Solo se afirma lo que la agencia confirmó: stands a medida
// (diseño, fabricación, montaje y desmontaje), modulares, portátiles y alquiler por evento; diseño
// incluido; producción con proveedores especializados (no taller propio); trabajo en todo el Perú.
import type { ContenidoServicio } from "@/lib/page-content/tipos";

export const contenido: ContenidoServicio = {
  actualizado: "2026-11-01",
  eyebrow: "Stands para ferias",
  h1: "Stands para ferias y exposiciones en Lima: a medida, modulares y en alquiler",
  intro:
    "Diseñamos, producimos y montamos stands para ferias, congresos y exposiciones en Lima y todo el Perú. Stands a medida, modulares y portátiles, en venta o en alquiler, con un solo equipo a cargo desde el diseño hasta el desmontaje.",
  description:
    "## Qué incluye nuestro servicio de stands para ferias\n\nUn stand de feria tiene que hacer tres cosas a la vez: que tu marca se vea desde el pasillo, que la gente entre y que tu equipo pueda atender y registrar contactos. Por eso no lo tratamos como una estructura aislada, sino como el espacio donde ocurre tu participación en la feria.\n\nNos encargamos del diseño, la producción, el transporte, el montaje y el desmontaje. Coordinamos con proveedores especializados la fabricación de cada parte —estructura, gráfica, iluminación y mobiliario— y supervisamos el armado para que el stand esté listo antes de que abra la feria.\n\n## Tipos de stands que ofrecemos\n\n- **Stands a medida:** diseñados para tu espacio y tu marca, con estructura, gráfica y mobiliario propios. Son la opción cuando la feria es clave para tu negocio y quieres diferenciarte.\n- **Stands modulares:** sistemas reutilizables que se adaptan a distintos metrajes. Convienen si participas en varias ferias al año y quieres amortizar la inversión.\n- **Stands portátiles:** displays, roll-ups, counters y estructuras desmontables, para espacios pequeños, puntos de venta o eventos donde necesitas armar y desarmar rápido.\n- **Alquiler de stands:** el stand por el tiempo que dura el evento, sin comprar ni almacenar la estructura.\n\n## Diseño de stands: cómo trabajamos\n\nEmpezamos por los datos de la feria: metraje, ubicación del espacio (esquina, isla o en línea), reglamento del organizador, fechas de montaje y lo que quieres lograr. Con eso te proponemos un diseño con vistas del stand y te mostramos cómo se verá tu marca antes de producir.\n\nAntes de fabricar revisamos con el organizador las restricciones de altura, peso, electricidad y horarios de montaje, para evitar cambios de último momento en el recinto. Ajustamos el diseño contigo hasta aprobarlo y recién entonces pasamos a producción.\n\n## Montaje, desmontaje y logística\n\nCoordinamos el transporte de la estructura y el material, el montaje en los horarios que fija el organizador y el desmontaje al cierre. Trabajamos en ferias de Lima y en otras ciudades del Perú; si la feria es fuera de Lima, incluimos la logística en la propuesta.\n\n## Stand, equipo y material: todo en una sola propuesta\n\nUn buen stand necesita gente que lo atienda. Podemos sumar [anfitrionas para eventos](/anfitrionas) para la recepción y el registro de visitantes, [impulsadoras y promotoras](/impulsadoras-promotoras) para presentar tu producto, una [activación BTL](/activaciones-btl) que atraiga público al espacio y [merchandising corporativo](/merchandising-corporativo) para entregar a los visitantes. Así contratas tu participación completa con un solo responsable.\n\n## Comprar o alquilar un stand\n\nSi participas en una o dos ferias al año, el alquiler suele ser más conveniente: no inviertes en la estructura ni pagas su almacenamiento. Si participas en varias, un stand modular propio se amortiza entre eventos. Te ayudamos a compararlo con números en la propuesta.\n\n[Solicita una cotización de tu stand](/cotizar) indicando la feria, el metraje, las fechas y si buscas comprar o alquilar. Te enviamos una propuesta con el diseño y un presupuesto desglosado en menos de 72 horas.",
  descriptionLayout: "articulo" as const,
  includes: [
    "Diseño del stand con vistas antes de producir",
    "Producción coordinada con proveedores especializados",
    "Transporte, montaje y desmontaje",
    "Revisión del reglamento de la feria",
    "Ferias en Lima y en todo el Perú",
  ],
  audience: [
    "Marcas que participan en ferias y exposiciones",
    "Empresas que exhiben en congresos o convenciones",
    "Equipos comerciales que van a varias ferias al año",
    "Marcas que necesitan un espacio en un centro comercial o evento",
  ],
  projectTypes: [
    {
      title: "Stands a medida",
      desc: "Diseño exclusivo para tu espacio, con estructura, gráfica y mobiliario.",
    },
    {
      title: "Stands modulares",
      desc: "Sistemas reutilizables que se adaptan a distintos metrajes y ferias.",
    },
    {
      title: "Stands portátiles",
      desc: "Displays, roll-ups y counters para espacios pequeños o montajes rápidos.",
    },
    {
      title: "Alquiler de stands",
      desc: "El stand por la duración del evento, sin comprar ni almacenar la estructura.",
    },
    {
      title: "Stands para congresos",
      desc: "Espacios de exhibición para congresos, convenciones y eventos sectoriales.",
    },
    {
      title: "Participación completa",
      desc: "Stand, anfitrionas, activación y merchandising en una sola propuesta.",
    },
  ],
  benefits: [
    {
      title: "Del diseño al desmontaje",
      desc: "Un solo equipo se encarga del diseño, la producción, el montaje y el desmontaje: no coordinas proveedores por separado.",
    },
    {
      title: "Sin sorpresas en el recinto",
      desc: "Revisamos el reglamento de la feria antes de fabricar: altura, electricidad y horarios de montaje.",
    },
    {
      title: "Un stand que atrae y convierte",
      desc: "Diseñamos el espacio para atender y registrar visitantes, y podemos sumar el personal que lo hace funcionar.",
    },
  ],
  faqs: [
    {
      q: "¿Qué tipos de stands para ferias ofrecen?",
      a: "Stands a medida, stands modulares, stands portátiles (displays, roll-ups y counters) y alquiler de stands por evento.",
    },
    {
      q: "¿Incluyen el diseño del stand?",
      a: "Sí. Proponemos el diseño con vistas del stand y lo ajustamos contigo hasta aprobarlo, antes de producir.",
    },
    {
      q: "¿Se encargan del montaje y el desmontaje?",
      a: "Sí. Coordinamos el transporte, el montaje en los horarios del organizador y el desmontaje al cierre de la feria.",
    },
    {
      q: "¿Conviene comprar o alquilar un stand?",
      a: "Si participas en una o dos ferias al año, el alquiler suele convenir más. Si participas en varias, un stand modular propio se amortiza entre eventos. Te lo comparamos en la propuesta.",
    },
    {
      q: "¿Hacen stands para ferias fuera de Lima?",
      a: "Sí, trabajamos en todo el Perú. Si la feria es fuera de Lima, incluimos el transporte y la logística en la propuesta.",
    },
    {
      q: "¿Pueden atender el stand durante la feria?",
      a: "Sí. Podemos sumar anfitrionas para la recepción y el registro, promotoras para presentar tu producto y una activación para atraer público.",
    },
    {
      q: "¿Cuánto cuesta un stand para ferias?",
      a: "Depende del metraje, el tipo de stand, los acabados, la duración de la feria y si es compra o alquiler. Te enviamos un presupuesto desglosado según tu brief en menos de 72 horas.",
    },
  ],
};
